import { useEffect, useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Button, Icon, TextField } from '@bloo-team/ui'
import { Trash2 } from '@bloo-team/ui/icons'
import { canDeleteConfig, canToggleStatus, isActive, isConfigured, pluginTitle } from '@bloo-team/api-client'
import type { Plugin } from '@bloo-team/api-client'
import { ConfirmStrip } from '../../../components/settings/ConfirmStrip'
import { Pagination } from '@bloo-team/ui'
import { PluginConfigForm } from '../../../components/settings/PluginConfigForm'
import { useCaseStatuses } from '../../../lib/case-statuses'
import {
  facetFilters,
  formMode,
  rowKey,
  useDeletePluginConfig,
  useMultiConfigNames,
  usePluginConfig,
  usePluginFacets,
  usePluginManifest,
  usePlugins,
  useSavePluginConfig,
  useSetPluginStatus,
} from '../../../lib/integrations'

/**
 * Settings · Integrations · Plugins (docs/settings-requirements.md §4;
 * docs/settings.md §Integrations).
 *
 * The platform plugin store — the connectors and credentials both products
 * consume (docs/IA.md IA3). Since D1 (docs/integrations-requirements.md,
 * 2026-09-26) *Integrations* means the catalog and its instances, and this
 * store is the section's fourth child, **Plugins** — the page itself is
 * unchanged, only its address moved (`/settings/integrations` still lands
 * here when SynthAI is not entitled). Not SynthAI's LLM connections, which
 * are a different backend and live in Settings → SynthAI.
 *
 * What exists, whether it is configured, whether the backend validated it, and
 * whether it is running — and, through the slide-over, how it is configured.
 * The console knows nothing about any individual integration: each plugin
 * describes its own form and PluginConfigForm renders it.
 *
 * Two row rules the list has to get right, both from the legacy screen:
 *
 *   title    a plugin that may hold several configs is titled by its
 *            CONFIG name; everything else by its display name.
 *   toggle   ON needs configured AND validated. OFF also accepts merely
 *            active — an integration that went invalid while running must
 *            still be stoppable.
 */

function PluginsPage() {
  const { clusterId, scopeId } = Route.useParams()

  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [pageNo, setPageNo] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [authors, setAuthors] = useState<string[]>([])
  const [tags, setTags] = useState<string[]>([])
  const [confirmDelete, setConfirmDelete] = useState<Plugin | null>(null)
  const [configuring, setConfiguring] = useState<Plugin | null>(null)

  // Server-side filter: settle before asking, or every keystroke is a POST.
  useEffect(() => {
    const t = setTimeout(() => setQuery(search), 250)
    return () => clearTimeout(t)
  }, [search])

  const filters = useMemo(() => facetFilters(authors, tags), [authors, tags])
  const list = usePlugins({ clusterId, scopeId, pageNo, pageSize, search: query, filters })
  const multi = useMultiConfigNames(clusterId, scopeId)
  const vocabulary = usePluginFacets(clusterId, scopeId)
  const setStatus = useSetPluginStatus()
  const remove = useDeletePluginConfig()
  const save = useSavePluginConfig()

  // The manifest is keyed by the MACHINE name; the saved values by id +
  // config_id. A plugin that has never been configured has no config to read,
  // so the form opens on the manifest defaults (both hooks stay disabled until
  // there is something to ask for).
  const manifest = usePluginManifest(clusterId, scopeId, configuring?.plugin_name ?? null)
  const caseStatuses = useCaseStatuses({ clusterId, scopeId })
  const savedConfig = usePluginConfig(
    clusterId, scopeId, configuring?.id ?? null, configuring?.config_id ?? null,
  )

  const rows = list.data?.plugins ?? []
  const total = list.data?.total ?? 0
  const multiNames = multi.data ?? []
  // From an UNFILTERED read, never from `rows`. Deriving these from the page
  // on screen collapses the menu as it is used: picking one tag removes every
  // chip absent from the narrowed result, so a facet can be cleared but never
  // widened or swapped.
  const facets = vocabulary.data ?? { authors: [], tags: [] }

  const resetPage = <T,>(set: (v: T) => void) => (v: T) => {
    set(v)
    setPageNo(1)
  }

  const toggleFacet = (list: string[], value: string) =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value]

  return (
    <div className="app-stack app-stack-wide">
      <section className="hud-panel sy-card" aria-label="Integrations">
        <div className="sy-pane-head">
          <span className="hud-eyebrow">Plugins</span>
          <span className="mono muted">{total}</span>
        </div>

        <div className="sy-toolbar">
          <TextField
            aria-label="Search integrations"
            value={search}
            onChange={resetPage(setSearch)}
            placeholder="Search name…"
          />
        </div>

        {facets.authors.length || facets.tags.length ? (
          <div className="set-facets">
            {facets.authors.length ? (
              <div className="set-facet" role="group" aria-label="Filter by author">
                <span className="hud-eyebrow">Author</span>
                {facets.authors.map((a) => (
                  <button
                    key={a}
                    type="button"
                    className="list-chip"
                    aria-pressed={authors.includes(a)}
                    onClick={() => resetPage(setAuthors)(toggleFacet(authors, a))}
                  >
                    {a}
                  </button>
                ))}
              </div>
            ) : null}
            {facets.tags.length ? (
              <div className="set-facet" role="group" aria-label="Filter by tag">
                <span className="hud-eyebrow">Tag</span>
                {facets.tags.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className="list-chip"
                    aria-pressed={tags.includes(t)}
                    onClick={() => resetPage(setTags)(toggleFacet(tags, t))}
                  >
                    {t}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {list.error ? (
          <p className="sy-error" role="alert">
            {list.error.message}
          </p>
        ) : null}

        {multi.error ? (
          // Without list_multi, pluginTitle falls back to display_name and two
          // configs of the same plugin render as identical rows with different
          // delete targets. Silent is not an option.
          <p className="sy-warning" role="alert">
            Could not read which plugins allow several configurations
            ({multi.error.message}). Rows are titled by plugin name, so two configurations of the
            same plugin will look alike — check the configuration before changing or deleting one.
          </p>
        ) : null}

        {!rows.length && !list.isLoading && !list.error ? (
          <p className="sy-help">
            {query || authors.length || tags.length
              ? 'No integration matches those filters.'
              : 'No integrations available in this scope.'}
          </p>
        ) : null}

        {rows.length ? (
          <div className="sy-table-wrap">
            <table className="dense-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Tags</th>
                  <th>Author</th>
                  <th>Validation</th>
                  <th>Configuration</th>
                  <th>Running</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const toggleable = canToggleStatus(p)
                  const active = isActive(p)
                  const title = pluginTitle(p, multiNames)
                  return (
                    // id alone is NOT unique: a multi-config plugin repeats it.
                    <tr key={rowKey(p)}>
                      <td>
                        {title}
                        {p.config_name && p.config_name !== p.display_name ? (
                          <span className="mono muted"> {p.display_name}</span>
                        ) : null}
                      </td>
                      <td>
                        {p.tag.length ? (
                          <span className="sy-pills">
                            {p.tag.map((t) => (
                              <span key={t} className="sy-pill">
                                {t}
                              </span>
                            ))}
                          </span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td className="mono muted">{p.created_by || '—'}</td>
                      <td>
                        {/* The boolean, never the message: the message is free
                            text the backend owns and the legacy page matched
                            on by hand. */}
                        <span className="sy-pill" data-tone={p.validated.validated ? 'good' : 'warn'}>
                          {p.validated.message || (p.validated.validated ? 'Validated' : 'Not validated')}
                        </span>
                      </td>
                      <td>
                        {isConfigured(p) ? (
                          <span className="sy-pill" data-on="">configured</span>
                        ) : (
                          <span className="muted">not configured</span>
                        )}
                      </td>
                      <td>
                        <label className="sy-check">
                          <input
                            type="checkbox"
                            role="switch"
                            checked={active}
                            disabled={!toggleable || setStatus.isPending}
                            aria-label={`${active ? 'Stop' : 'Start'} ${title}`}
                            onChange={() =>
                              setStatus.mutate({
                                clusterId,
                                scopeId,
                                id: p.id,
                                configId: p.config_id,
                                status: active ? 'INACTIVE' : 'ACTIVE',
                              })
                            }
                          />
                          <span className="muted">{active ? 'running' : 'stopped'}</span>
                        </label>
                      </td>
                      <td>
                        <div className="sy-controls">
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={`Configure ${title}`}
                            onPress={() => { save.reset(); setConfiguring(p) }}
                          >
                            {isConfigured(p) ? 'Configure' : 'Set up'}
                          </Button>
                          {canDeleteConfig(p) ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              tone="warn"
                              aria-label={`Delete configuration ${title}`}
                              onPress={() => { remove.reset(); setConfirmDelete(p) }}
                            >
                              <Icon icon={Trash2} size={13} />
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : null}

        {total > 0 ? (
          <Pagination
            label="Integration pagination"
            page={pageNo}
            pageSize={pageSize}
            total={total}
            busy={list.isFetching}
            unit="integrations"
            onPageChange={setPageNo}
            onPageSizeChange={(n) => {
              setPageSize(n)
              setPageNo(1)
            }}
          />
        ) : null}

        {setStatus.error ? (
          <p className="sy-error" role="alert">
            {setStatus.error.message}
          </p>
        ) : null}
        {remove.error ? (
          <p className="sy-error" role="alert">
            {remove.error.message}
          </p>
        ) : null}

        {confirmDelete ? (
          <ConfirmStrip
            label={`Delete ${pluginTitle(confirmDelete, multiNames)}?`}
            message={
              <>
                Delete <b>{pluginTitle(confirmDelete, multiNames)}</b>? This removes the
                configuration, not the plugin — {confirmDelete.display_name} stays available to
                configure again.
              </>
            }
            confirmLabel="Delete"
            cancelLabel="Keep"
            tone="warn"
            busy={remove.isPending}
            onCancel={() => setConfirmDelete(null)}
            onConfirm={() =>
              remove.mutate(
                {
                  clusterId,
                  // The ROUTE's scope, matching the legacy screen, and matching
                  // the status change beside it — a plugin list is requested
                  // for one scope, so both writes address that scope. (Tokens
                  // does the opposite because its legacy screen passed the
                  // row's scope; the two APIs genuinely differ. See G6.)
                  scopeId,
                  id: confirmDelete.id,
                  // canDeleteConfig has already established this is set.
                  configId: confirmDelete.config_id as string,
                },
                { onSuccess: () => setConfirmDelete(null) },
              )
            }
          />
        ) : null}

        <p className="sy-help">
          Search matches a configuration's name. A plugin that holds only one configuration has no
          configuration name, so search will not find it — filter by tag or author instead.
        </p>
        <p className="sy-help">
          Author and tag filters are read once, unfiltered, from the first page of integrations.
          There is no endpoint that serves the full vocabulary, so a value that appears only on a
          later page will not be offered here.
        </p>
      </section>

      <PluginConfigForm
        open={configuring !== null}
        plugin={configuring}
        fields={manifest.data ?? []}
        saved={savedConfig.data ?? {}}
        caseStatuses={caseStatuses.data}
        mode={configuring ? formMode(configuring) : 'basic'}
        loading={manifest.isLoading || savedConfig.isLoading}
        busy={save.isPending}
        error={save.error?.message ?? null}
        loadError={manifest.error?.message ?? savedConfig.error?.message ?? null}
        onClose={() => setConfiguring(null)}
        onSubmit={({ data, file }) => {
          if (!configuring) return
          save.mutate(
            {
              clusterId,
              scopeId,
              id: configuring.id,
              configId: configuring.config_id,
              data,
              file,
            },
            { onSuccess: () => setConfiguring(null) },
          )
        }}
      />
    </div>
  )
}

export const Route = createFileRoute('/$clusterId/$scopeId/settings/integrations/plugins')({
  component: PluginsPage,
})
